import { z } from "zod";
import {
  businessRoleSchema,
  businessStatusSchema,
  inviteKindSchema,
  membershipStatusSchema,
  jobTitleSchema,
  paymentOnboardingStatusSchema,
  sportSchema,
  bookingForSchema,
  classFillSchema,
} from "../enums.ts";
import { isoDateTimeSchema, timestampsSchema, uuidSchema } from "./common.ts";
import { FIRST_CLASS_WELCOME_HOURS, FIRST_CLASS_WELCOME_MAX_TEXT, FIRST_CLASS_WELCOME_MIN_TEXT } from "../first-class-welcome.ts";
import { WAITLIST_ACCEPT_MINUTES_MAX, WAITLIST_ACCEPT_MINUTES_MIN, WAITLIST_LATE_HOURS_MAX, WAITLIST_LATE_HOURS_MIN } from "../waitlist-rules.ts";
import { campaignTokenSchema, heardFromSchema, REFERRER_NAME_MAX } from "../signup-source.ts";
import { frontDeskAccessSchema } from "../front-desk-access.ts";

export const firstClassWelcomeSchema = z.object({
  on: z.boolean(),
  hoursBefore: z
    .number()
    .int()
    .refine((value) => (FIRST_CLASS_WELCOME_HOURS as readonly number[]).includes(value), "Pick one of the hours on the list"),
  text: z.string().trim().min(FIRST_CLASS_WELCOME_MIN_TEXT).max(FIRST_CLASS_WELCOME_MAX_TEXT),
});

export const businessSettingsSchema = z.object({
  allowAnyGroup: z.boolean(),
  /**
   * Only matters while `allowAnyGroup` is off. On: a child with no group yet can still be booked
   * into any group's session; off: such a child sees no group session until the owner places them.
   * The owner's choice (2026-10-04). On by default, so nothing changes for a family until the owner
   * decides. Optional for older API builds.
   */
  allowBookingWithoutGroup: z.boolean().default(true),
  allowBookingWhileOwed: z.boolean(),
  renewsOnDay: z.number().int().min(1).max(28),
  /**
   * The module "Coaches add their swimmers" (Settings, Modules): coaches type the names of their
   * swimmers under their levels, and the Staff page has the card that asks them (coach-adds-swimmers,
   * 2026-10-09). A module since 2026-11-03: off by default, and a missing value means off.
   */
  staffCanAddParticipants: z.boolean().optional(),
  /**
   * Approve each cover (cover requests, migration 0072). Off: the first yes of a staff member takes
   * the session. On: the owner or an admin approves the yes first. Off by default. Optional for
   * older API builds: a missing value means off.
   */
  coverNeedsApproval: z.boolean().optional(),
  /**
   * Add anyway (#ai-feedback 2026-10-26): staff who are not the owner or an admin may add a client
   * to a session when no package pays for it. Off by default. Optional for older API builds: a
   * missing value means off.
   */
  staffCanAddAnyway: z.boolean().optional(),
  /**
   * Carry over (#ai-feedback 2026-10-26, card #1): at the end of a package, its unused sessions move
   * to the package that waits for it. With no package that waits, they end with the package. Off by
   * default. Optional for older API builds: a missing value means off.
   */
  carryOverSessions: z.boolean().optional(),
  /**
   * The welcome before a person's first class (C3, `first-class-welcome.ts`). Answers always carry
   * it with the start values filled in (on where clients book for themselves). Optional for older
   * API builds.
   */
  firstClassWelcome: firstClassWelcomeSchema.optional(),
  /**
   * Check-in by name (I1): 10 minutes after the start, the job marks the clients with no mark absent.
   * Off by default (`AUTO_ABSENT_DEFAULT`). Optional for older API builds.
   */
  autoAbsent: z.boolean().optional(),
  /**
   * Waitlist offers (B2): a place that opens fewer than this many hours before the start goes to the
   * first person in line as an offer, not as a booking. Default 12. Optional for older API builds.
   */
  waitlistLateHours: z.number().int().min(WAITLIST_LATE_HOURS_MIN).max(WAITLIST_LATE_HOURS_MAX).optional(),
  /**
   * The time to accept an offer, in minutes. Default 120. The offer ends earlier when the session
   * starts within `WAITLIST_OFFER_CUTOFF_HOURS` of that. Optional for older API builds.
   */
  waitlistAcceptMinutes: z.number().int().min(WAITLIST_ACCEPT_MINUTES_MIN).max(WAITLIST_ACCEPT_MINUTES_MAX).optional(),
  /**
   * What the Front desk role may see and do (Settings, Front desk; `front-desk-access.ts`). Answers
   * always carry every item, with the start values filled in. A save may send some items: the API
   * merges them into the stored ones. Owner and admin only, like every field here. Optional for
   * older API builds.
   */
  frontDesk: frontDeskAccessSchema.optional(),
});
export type BusinessSettings = z.infer<typeof businessSettingsSchema>;

/**
 * The ten words a business picks for its people and its groups. `business`, `staff`, `member`,
 * `participant` and `group` are the system names; each has a singular and a plural label a person
 * reads. `useLabels()` in each app is the only place that should read these off a Business.
 */
export const businessLabelsSchema = z.object({
  businessSingular: z.string(),
  businessPlural: z.string(),
  staffSingular: z.string(),
  staffPlural: z.string(),
  memberSingular: z.string(),
  memberPlural: z.string(),
  participantSingular: z.string(),
  participantPlural: z.string(),
  groupSingular: z.string(),
  groupPlural: z.string(),
});
export type BusinessLabels = z.infer<typeof businessLabelsSchema>;

const labelWordSchema = z
  .string()
  .trim()
  .min(1)
  .max(24)
  .regex(/^[a-zA-Z ]+$/, "Letters and spaces only");

/** `PATCH /v1/business/businesses/:businessId/labels`. Any subset; singular and plural travel together. */
export const updateBusinessLabelsRequestSchema = z
  .object({
    business: z.object({ singular: labelWordSchema, plural: labelWordSchema }).optional(),
    staff: z.object({ singular: labelWordSchema, plural: labelWordSchema }).optional(),
    member: z.object({ singular: labelWordSchema, plural: labelWordSchema }).optional(),
    participant: z.object({ singular: labelWordSchema, plural: labelWordSchema }).optional(),
    group: z.object({ singular: labelWordSchema, plural: labelWordSchema }).optional(),
  })
  .partial();
export type UpdateBusinessLabelsRequest = z.infer<typeof updateBusinessLabelsRequestSchema>;

// ---------------------------------------------------------------------------
// Logo (migration 0026)
// ---------------------------------------------------------------------------

/** The largest PNG file the API takes for each shape of a logo, in bytes. */
export const LOGO_MAX_BYTES = 600 * 1024;
/** The full logo is at most this many pixels on its long side; the square is exactly 512. */
export const LOGO_FULL_MAX_SIDE = 1024;
export const LOGO_MARK_SIDE = 512;

const hexColourSchema = z.string().regex(/^#[0-9a-f]{6}$/, "A colour like #1a2b3c");

/**
 * The live logo of a business, as every reader gets it. Null on a business means "show the
 * initials". The URLs are absolute and never change their picture, because each upload is a new
 * logo with a new id, so a browser can keep them for a year.
 */
export const businessLogoSchema = z.object({
  id: uuidSchema,
  /** The whole logo, trimmed. For the invite page and wide places. */
  fullUrl: z.string(),
  /** The square version, 512 x 512. For the sidebar, headers and lists. */
  markUrl: z.string(),
  fullWidth: z.number().int().positive(),
  fullHeight: z.number().int().positive(),
  /** Put the logo on a small white plate on dark screens. */
  plateOnDark: z.boolean(),
  /** The logo's main colour, for thin lines and the owner Home greeting band (darkened for AA). Buttons stay Teamna blue. */
  accent: hexColourSchema.nullable(),
});
export type BusinessLogo = z.infer<typeof businessLogoSchema>;

/** One upload, with when it was made. */
export const businessLogoVersionSchema = businessLogoSchema.extend({ createdAt: isoDateTimeSchema });
export type BusinessLogoVersion = z.infer<typeof businessLogoVersionSchema>;

/** `GET /v1/owner/businesses/:businessId/logos`: the live logo and the recent uploads, newest first. */
export const businessLogosResponseSchema = z.object({
  activeLogoId: uuidSchema.nullable(),
  items: z.array(businessLogoVersionSchema),
});
export type BusinessLogosResponse = z.infer<typeof businessLogosResponseSchema>;

/** A PNG file as base64, no `data:` prefix. */
const pngBase64Schema = z
  .string()
  .min(16)
  .max(Math.ceil(LOGO_MAX_BYTES / 3) * 4, "The file is too large")
  .regex(/^[A-Za-z0-9+/]+={0,2}$/, "Send the file as base64");

/**
 * `POST /v1/owner/businesses/:businessId/logos`. The browser makes both files from the owner's
 * upload; the new logo goes live at once.
 */
export const uploadBusinessLogoRequestSchema = z.object({
  full: pngBase64Schema,
  mark: pngBase64Schema,
  plateOnDark: z.boolean(),
  accent: hexColourSchema.nullable(),
  /** The uploader owns the logo or has permission to use it. */
  rightsConfirmed: z.literal(true),
});
export type UploadBusinessLogoRequest = z.infer<typeof uploadBusinessLogoRequestSchema>;

/** `PUT /v1/owner/businesses/:businessId/logos/active`: an earlier upload, or null for initials. */
export const setActiveLogoRequestSchema = z.object({ logoId: uuidSchema.nullable() });
export type SetActiveLogoRequest = z.infer<typeof setActiveLogoRequestSchema>;

/**
 * The colour of the Teamna app for one business (mobile V2, slice 2.2): buttons, links and other
 * accent places on the phone, for everyone in the business. Null is Teamna blue. Any colour is safe:
 * the apps make it darker or lighter until text on it passes WCAG AA (`paletteFromAccent`).
 */
export const appAccentSchema = hexColourSchema.nullable();

/** `PUT /v1/owner/businesses/:businessId/app-accent` */
export const setAppAccentRequestSchema = z.object({ accent: appAccentSchema });
export type SetAppAccentRequest = z.infer<typeof setAppAccentRequestSchema>;

export const appAccentResponseSchema = z.object({ appAccent: appAccentSchema });
export type AppAccentResponse = z.infer<typeof appAccentResponseSchema>;

export const businessSchema = z
  .object({
    id: uuidSchema,
    slug: z.string(),
    name: z.string(),
    timezone: z.string(),
    currency: z.string(),
    paymentAccountId: z.string().nullable(),
    paymentOnboardingStatus: paymentOnboardingStatusSchema,
    status: businessStatusSchema,
    sport: sportSchema,
    /** Who books (migration 0069). Screens read it through `useShape()`, never the sport. */
    bookingFor: bookingForSchema,
    /** How classes fill (migration 0069). */
    classFill: classFillSchema,
    venueName: z.string().nullable(),
    /** Weekdays the business is closed, 0 = Sunday. */
    daysOff: z.array(z.number().int().min(0).max(6)),
    settings: businessSettingsSchema,
    /** The join code members type or tap. Null until the first invite exists. */
    inviteCode: z.string().nullable(),
    labels: businessLabelsSchema,
  })
  .merge(timestampsSchema);
export type Business = z.infer<typeof businessSchema>;

/** `POST /v1/owner/businesses`. The caller becomes the first owner. */
export const createBusinessRequestSchema = z.object({
  // Trimmed first, so "  " is refused and every client stores the same name (review F3.3).
  name: z.string().trim().min(2).max(120),
  slug: z
    .string()
    .min(2)
    .max(60)
    .regex(/^[a-z0-9][a-z0-9-]*[a-z0-9]$/, "Lowercase letters, digits, and hyphens only"),
  timezone: z.string().min(3).max(64).default("Asia/Dubai"),
  currency: z.string().length(3).default("AED"),
  sport: sportSchema.default("other"),
  /**
   * The business shape (migration 0069). Desk asks it after the sport, filled from the sport. Left
   * out, each one is the sport's start value (`defaultShape`), so an older client still works.
   */
  bookingFor: bookingForSchema.optional(),
  classFill: classFillSchema.optional(),
  venueName: z.string().max(120).nullable().optional(),
  daysOff: z.array(z.number().int().min(0).max(6)).optional(),
  /** "Your role at the business" on the owner sign-up. A label only: the caller is the owner. */
  jobTitle: jobTitleSchema.optional(),
  /**
   * "How did you hear about Teamna?" (task 0.7). Desk web always sends it: the sign-up asks it and
   * does not continue without an answer. Optional here, so a mobile build without the question can
   * still create a business (the answer is then null).
   */
  heardFrom: heardFromSchema.optional(),
  /** The owner who told them, typed. Kept only with `heardFrom: another_owner`. Never an analytics property. */
  referrerName: z.string().trim().max(REFERRER_NAME_MAX).nullable().optional(),
  /** The website campaign that brought the person (first touch, carried by "Start free"). A bad value is null. */
  utmSource: campaignTokenSchema.optional(),
  utmMedium: campaignTokenSchema.optional(),
  utmCampaign: campaignTokenSchema.optional(),
});
export type CreateBusinessRequest = z.infer<typeof createBusinessRequestSchema>;

export const updateBusinessRequestSchema = z
  .object({
    name: z.string().min(2).max(120),
    timezone: z.string().min(3).max(64),
    status: businessStatusSchema,
    sport: sportSchema,
    /** The business shape (migration 0069). Owner and admin only, like every field here. Audited. */
    bookingFor: bookingForSchema,
    classFill: classFillSchema,
    venueName: z.string().max(120).nullable(),
    daysOff: z.array(z.number().int().min(0).max(6)),
    settings: businessSettingsSchema.partial(),
  })
  .partial();
export type UpdateBusinessRequest = z.infer<typeof updateBusinessRequestSchema>;

export const inviteSchema = z
  .object({
    id: uuidSchema,
    businessId: uuidSchema,
    code: z.string(),
    kind: inviteKindSchema,
    maxUses: z.number().int().nullable(),
    uses: z.number().int(),
    expiresAt: isoDateTimeSchema.nullable(),
    createdBy: uuidSchema.nullable(),
    /** Ready to paste into WhatsApp. */
    url: z.string().url(),
  })
  .merge(timestampsSchema);
export type Invite = z.infer<typeof inviteSchema>;

export const createInviteRequestSchema = z.object({
  kind: inviteKindSchema.default("member"),
  maxUses: z.number().int().min(1).max(1000).nullable().default(null),
  expiresInDays: z.number().int().min(1).max(365).nullable().default(30),
});
export type CreateInviteRequest = z.infer<typeof createInviteRequestSchema>;

/** One row of the owner's member list. */
export const membershipSchema = z
  .object({
    id: uuidSchema,
    businessId: uuidSchema,
    userId: uuidSchema,
    status: membershipStatusSchema,
    joinedViaInviteId: uuidSchema.nullable(),
    fullName: z.string().nullable(),
    /** Null for a person who signs in by email only. */
    phoneE164: z.string().nullable(),
    participants: z.array(
      z.object({ id: uuidSchema, displayName: z.string(), kind: z.enum(["self", "child"]) }),
    ),
  })
  .merge(timestampsSchema);
export type Membership = z.infer<typeof membershipSchema>;

/** `PATCH /v1/owner/businesses/:businessId/members/:id`: pause, reactivate, or remove. */
export const updateMembershipRequestSchema = z.object({
  status: membershipStatusSchema,
});
export type UpdateMembershipRequest = z.infer<typeof updateMembershipRequestSchema>;

/**
 * `GET /v1/owner/businesses/:businessId/payment-onboarding` (read only), and the answer of
 * `POST .../payment-onboarding/link` (makes the account when there is none, and a fresh
 * single-use link) and `POST .../payment-onboarding/refresh` (reads the account again now).
 */
export const paymentOnboardingResponseSchema = z.object({
  status: paymentOnboardingStatusSchema,
  /** `fake` or `stripe`. */
  provider: z.string(),
  accountId: z.string().nullable(),
  /** Only on `POST .../link`. Single use; it expires within minutes. */
  onboardingUrl: z.string().url().nullable(),
  /** True when members can pay this business by card now. */
  cardPayments: z.boolean(),
  /**
   * True while Teamna has card payments off for all businesses (Control Center switch): the Get paid
   * page shows "Coming soon" and the link route refuses with CARD_PAYMENTS_COMING_SOON.
   */
  comingSoon: z.boolean().optional(),
  /** What the provider said the last time Teamna read the account. Null before the first read. */
  account: z
    .object({
      legalName: z.string().nullable(),
      bankName: z.string().nullable(),
      bankLast4: z.string().nullable(),
      /** `daily`, `weekly`, `monthly` or `manual` (no payout unless the business starts one). */
      payoutInterval: z.string().nullable(),
      payoutDelayDays: z.number().int().nullable(),
      chargesEnabled: z.boolean(),
      payoutsEnabled: z.boolean(),
      /** When the provider wants the tasks done. */
      deadline: isoDateTimeSchema.nullable(),
      /** What the owner must do, in plain words. */
      tasks: z.array(z.string()),
      checkedAt: isoDateTimeSchema,
    })
    .nullable(),
  /** When the business was first verified. */
  verifiedAt: isoDateTimeSchema.nullable(),
});
export type PaymentOnboardingResponse = z.infer<typeof paymentOnboardingResponseSchema>;
